import React, { useLayoutEffect } from 'react';
import ScrollToTopButton from './common/ScrollToTopButton';

const PrivacyPolicy = ({ onBack }) => {
  // Force scroll to top on mount - useLayoutEffect runs synchronously before paint
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div 
      data-public-scroll-container="true"
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Premium Legal Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 pb-4 mb-8 gap-4">
          <div className="flex items-center gap-3">
            <img src="/new_icon+text_color.png" alt="CraftMyParty" className="h-12 sm:h-14 w-auto" />
            <div className="h-6 w-px bg-gray-200 hidden sm:block"></div>
            <span className="hidden sm:inline-block text-xs font-semibold text-gray-400 uppercase tracking-wider">Your Party, Made Personal.</span>
          </div>
          <nav className="flex items-center gap-6 text-sm font-bold">
            <a href="/home" className="text-gray-600 hover:text-purple-600 transition">Home</a>
            <a href="/privacy" className="text-purple-600 border-b-2 border-purple-600 pb-0.5 transition">Privacy</a>
            <a href="/terms" className="text-gray-600 hover:text-purple-600 transition">Terms</a>
            <a href="/faq" className="text-gray-600 hover:text-purple-600 transition">FAQ</a>
          </nav>
        </header>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600">Last Updated: December 25th, 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 md:p-10 prose prose-sm sm:prose max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              CraftMyParty ("we", "our", or "us") values your privacy. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use CraftMyParty, including through Pinterest API integration. By using our service you agree to this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Information You Provide</h3>
            <p className="text-gray-700 leading-relaxed mb-3">We collect information you choose to provide, including:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Account information</strong> (name, email, authentication credentials)</li>
              <li><strong>Event planning inputs</strong> such as event type, budget range, guest count, and preferences</li>
              <li><strong>Moodboard or Pinterest‐linked data</strong> you consent to share (such as boards or selected Pins when you authenticate via Pinterest OAuth)</li>
              <li><strong>Support communications</strong> you send us</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Information Collected Automatically</h3>
            <p className="text-gray-700 leading-relaxed">
              We collect usage data (pages visited, features used), device and technical data (browser, OS, anonymized IP), and session data stored in your browser's local storage to operate and improve the service.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.3 Third-Party Authentication</h3>
            <p className="text-gray-700 leading-relaxed">
              If you sign in using a third-party provider (like Google, Apple, or Pinterest), we may receive basic profile information (name, email) and, with your explicit consent, your Pinterest boards or moodboard data to influence party theme suggestions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We use your data to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Provide and operate CraftMyParty services, including theme and moodboard features</li>
              <li>Generate personalized event ideas and recommendations using AI and connected data</li>
              <li>Improve product functionality and user experience</li>
              <li>Respond to support inquiries and service updates</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">
              Data received from the Pinterest API is used only for content generation and personalization within your event planning experience. We do not sell personal data or use it for advertising. We will not use your Pinterest data for model training in external AI systems.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Pinterest API Integration</h2>
            <p className="text-gray-700 leading-relaxed mb-3">When you connect your Pinterest account:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>We request your consent before accessing or storing your Pinterest board or moodboard data.</li>
              <li>We use OAuth 2.0 to obtain access tokens, and you may revoke access at any time through your account settings.</li>
              <li>We do not access or store your Pinterest login credentials.</li>
              <li>We clearly disclose what Pinterest data will be used, and you can opt out at any time.</li>
              <li>We do not share your Pinterest data with others outside CraftMyParty unless required by law.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Pinterest may use cookies and similar technologies when you interact with their services. You remain subject to Pinterest's own privacy settings and policy when using their platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Cookies and Browser Storage</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We use browser storage technologies (localStorage and sessionStorage) to store essential information for the operation of our service, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Authentication tokens</strong> - To maintain your login session and authenticate API requests</li>
              <li><strong>User account information</strong> - To personalize your experience and remember your preferences</li>
              <li><strong>Session data</strong> - To track your current plan and maintain your workflow state</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">
              This storage is essential for the functionality of CraftMyParty. Without it, you would need to log in on every page visit and your work-in-progress plans would not be saved. You can clear this data at any time through your browser settings or by logging out of your account.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Note:</strong> We do not currently use traditional cookies for tracking or analytics. Third-party services (such as Pinterest) may use cookies when you interact with their platforms, and you remain subject to their own privacy policies and cookie settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Storage and Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We store data on secure cloud infrastructure and retain it only as long as necessary to provide services or comply with legal requirements. Reasonable safeguards protect your information, though no system is entirely secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              You may have rights under laws such as GDPR, CCPA, and CPRA, including access, correction, deletion, restriction, and portability of personal data. For California residents, we comply with CCPA/CPRA requirements, including providing opt-out rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              CraftMyParty is not intended for children under 13 (US) or under 16 (EEA/UK). We do not knowingly collect personal data from children; if discovered, it will be deleted.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information may be processed in countries other than where you reside. We take appropriate safeguards aligned with applicable laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy and will provide notice of material changes. Continued use of our services after changes indicates acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-3">For privacy requests or questions:</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Contact Information</p>
              <p className="text-gray-700">Email: support@craftmyparty.com</p>
              <p className="text-gray-700">Address: Simi Valley, California, USA</p>
            </div>
          </section>
        </div>
      </div>
      <ScrollToTopButton targetSelector="[data-public-scroll-container='true']" />
    </div>
  );
};

export default PrivacyPolicy;
